import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DiOccApiUrl } from '@di';
import { environment } from '@env/environment';
import type { UserResponse, UserToken } from '@models/auth.model';
import type {
  GsmDownload,
  GsmDownloadBody,
  GsmDownloadDataSheetsBody,
  GsmMediaType,
} from '@models/gsm.model';
import { AlertType, GsmBulkOption } from '@models/gsm.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FoerchAlertService } from '@services/foerch-alert.service';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';
import { FoerchGsmLocalStorageService } from '../foerch-gsm-local-storage.service';

/**
 * OCC commerce cloud service
 */
@Injectable()
export class FoerchOccService {
  private readonly _alertService = inject(FoerchAlertService);
  private readonly _apiUrl = inject(DiOccApiUrl);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _http = inject(HttpClient);
  private readonly _localStorageService = inject(FoerchGsmLocalStorageService);
  private readonly _modalService = inject(NgbModal);

  private readonly _currentUserPath =
    this._apiUrl + '/rest/v2/foerch/users/current';
  private readonly _deLocale = 'lang=de_DE&curr=EUR';

  private readonly _downloadEmailSuccessText =
    'In Kürze senden wir Ihnen einen Link zum Download der Dokumente an die angegebenen E-Mail-Adressen.';
  private readonly _downloadEmailFailText =
    'Beim Übermitteln der Daten ist etwas schief gelaufen. Bitte versuchen Sie es erneut.';

  /**
   *
   * @param userId
   * @param password
   * @returns true is logged in successfully
   */
  login(userId: string, password: string): Observable<boolean> {
    return this.loadToken(userId, password).pipe(
      catchError(() => of(null)),
      concatMap(val => this.loadUserUnitId(val)),
      takeUntilDestroyed(this._destroyRef)
    );
  }

  getOperatingInstructionUrl(
    productCode: string,
    abbreviation: string
  ): Observable<string> {
    const url = `${this._currentUserPath}/hazardousgoods/action/download/operating-instruction?productCode=${productCode}&abbreviation=${abbreviation}&${this._deLocale}`;

    return this._http
      .get<string>(url, {
        headers: this.getAccessHeaders(),
      })
      .pipe(
        map(source =>
          new RegExp(/^http(s)?:\/\//i).test(source)
            ? source
            : `${this._apiUrl}${source}`
        )
      );
  }

  getDataSheetTypes(): Observable<GsmMediaType[]> {
    const url = `${this._currentUserPath}/customerarticleSources?fields=BASIC&${this._deLocale}`;

    return this._http
      .get<any>(url, {
        headers: this.getAccessHeaders(),
      })
      .pipe(map(v => v?.mediaTypes ?? []));
  }

  /**
   * Requests the download of documents from backend.
   * Returns a download process ID. The document will be sent async via WebSocket.
   *
   * @param option The bulk option that triggered the download
   * @param body The data that will be sent to the endpoint
   */
  downloadDocuments(
    option: GsmBulkOption,
    body: GsmDownloadDataSheetsBody | GsmDownloadBody
  ): Observable<string> {
    const endpointMapping = {
      [GsmBulkOption.DOWNLOAD_DATASHEETS]: 'data-sheets',
      [GsmBulkOption.DOWNLOAD_COLLECTIVE_OPERATING_INSTRUCTIONS]:
        'operating-instructions',
      [GsmBulkOption.DOWNLOAD_HAZARDOUS_GOODS_LIST]: 'hazardous-goods-register',
    };

    const endpoint: string = endpointMapping[option];
    const url = `${this._currentUserPath}/hazardousgoods/action/download/${endpoint}?${this._deLocale}`;

    return this._http.post<string>(url, body, {
      headers: this.getAccessHeaders(),
    });
  }

  /**
   * Sends request to the backend to send documents to email recipients.
   *
   * @param processId Download process ID
   * @param enteredEmails Email recipient addresses
   */
  sendDownloadToEmails(processId: string, enteredEmails: string[]): void {
    const url = `${this._currentUserPath}/hazardousgoods/action/download/email?${this._deLocale}`;

    const body = {
      processId,
      enteredEmails,
    };

    this._http
      .post(url, body, {
        headers: this.getAccessHeaders(),
      })
      .pipe(take(1))
      .subscribe({
        next: (downloadLink?: string) => {
          if (!downloadLink) {
            // no download link - download is still being processed
            this._modalService.dismissAll();
            this._alertService.showAlert(
              AlertType.SUCCESS,
              this._downloadEmailSuccessText,
              5000
            );
          } else {
            // we've got a download URL and trigger an update to the state
            const download: GsmDownload = {
              processId,
              downloadLink,
            };
            this._localStorageService.setDownloadLink(download);
          }
        },
        error: () => {
          // close modal in case of an error
          this._modalService.dismissAll();
          this._alertService.showAlert(
            AlertType.DANGER,
            this._downloadEmailFailText,
            5000
          );
        },
      });
  }

  /**
   *
   * @param res
   * @returns true if user loaded and set to session
   */
  private loadUserUnitId(res: UserToken): Observable<boolean> {
    if (res && res.access_token) {
      this._localStorageService.setAccessToken(res.access_token);
      const url =
        this._currentUserPath +
        '?fields=unit(uid,hazardousGoodsPortalConfig(logo(FULL), title))&' +
        this._deLocale;

      return this._http
        .get<UserResponse>(url, {
          headers: this.getAccessHeaders(res.access_token),
        })
        .pipe(
          catchError(() => of(null)),
          map((userResponse: UserResponse) => {
            if (userResponse && userResponse.unit && userResponse.unit.uid) {
              this._localStorageService.setUserUnitId(userResponse.unit.uid);
              this._localStorageService.setCustomPortalLogoUrl(
                userResponse.unit.hazardousGoodsPortalConfig
              );
              this._localStorageService.setCustomPortalTitle(
                userResponse.unit.hazardousGoodsPortalConfig
              );
              return true;
            }
            return false;
          })
        );
    }
    return of(false);
  }

  private loadToken(userId: any, password: any): Observable<UserToken> {
    const url: string = this._apiUrl + '/authorizationserver/oauth/token';
    const params = new HttpParams()
      .set('client_id', encodeURIComponent(environment.client_id))
      .set('client_secret', encodeURIComponent(environment.client_secret))
      .set('grant_type', 'password')
      .set('username', userId)
      .set('password', password);
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this._http.post<UserToken>(url, params, { headers });
  }

  private getAccessHeaders(accessToken?: string): HttpHeaders {
    if (!accessToken) {
      accessToken = this._localStorageService.getAccessToken();
    }
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    });
  }
}
